import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useAlerts } from '@/hooks/useAlerts';
import { approvalsApi } from '@/api/services/approvals.api';
import { cn } from '@/lib/utils';
import { useTheme } from '@/context/ThemeContext';
import { NAV_WIDTHS, type NavWidth } from '@/lib/appearance';
import { NAV_GROUPS } from '@/lib/modules';
import { AppearancePanel } from '@/components/common/AppearancePanel';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard,
  Drill,
  Package,
  Truck,
  Wrench,
  ShoppingCart,
  MapPin,
  AlertTriangle,
  Settings,
  LogOut,
  User,
  Bell,
  Search,
  Menu,
  Palette,
  FileSpreadsheet,
} from 'lucide-react';

/**
 * One destination in the rail.
 *
 * `permission` is the permission the server enforces on the module behind the
 * link. The rail hides what a person cannot use: showing a door that answers
 * 403 is worse than not showing it, and it made every role navigate the same
 * 57-item map regardless of what they were allowed to do.
 *
 * Entries with no permission are open to anyone signed in — either because the
 * module guards with JwtAuthGuard alone, or because the page is a landing
 * surface rather than data.
 */
interface NavLeaf {
  title: string;
  href: string;
  permission?: string;
  /** Renders a live count beside the label. */
  badge?: 'approvals';
  /**
   * One line saying what the screen is for. "Putaway" and "Pipe Tally" name a
   * noun and explain nothing; this is what turns the rail from a list of words
   * into something a new planner can read.
   */
  description?: string;
}

interface NavItem {
  title: string;
  icon: React.ReactNode;
  href?: string;
  permission?: string;
  /** Hide from anyone who has this permission — see NavGroup.excludePermission. */
  excludePermission?: string;
  badge?: 'approvals';
  /** What the whole group is for, shown once at the top of the flyout. */
  blurb?: string;
  children?: NavLeaf[];
}

/**
 * Built from the module registry rather than written out here.
 *
 * The rail, the "what is this" popover on every page header and the guide at
 * /guide all read `lib/modules.ts`, so a screen cannot be labelled one way in
 * the rail and described another way on the page — the failure that made the
 * old hand-maintained array drift from what the screens actually did.
 *
 * Groups are ordered along the work itself: what needs a person now, what we
 * have been asked to do, what we hold, where it sits, what carries it, what
 * pays for it, what permits it, then setup. Somebody reading down the rail is
 * walking the operation in order, which is a different and better organising
 * idea than "how often is this opened".
 */
const GROUP_ICONS: Record<string, React.ReactNode> = {
  LayoutDashboard: <LayoutDashboard className="h-5 w-5" />,
  Drill: <Drill className="h-5 w-5" />,
  Package: <Package className="h-5 w-5" />,
  Warehouse: <MapPin className="h-5 w-5" />,
  Wrench: <Wrench className="h-5 w-5" />,
  Truck: <Truck className="h-5 w-5" />,
  Handshake: <FileSpreadsheet className="h-5 w-5" />,
  ShoppingCart: <ShoppingCart className="h-5 w-5" />,
  ShieldCheck: <AlertTriangle className="h-5 w-5" />,
  Settings: <Settings className="h-5 w-5" />,
};

const navigation: NavItem[] = NAV_GROUPS.map((group) => ({
  title: group.title,
  icon: GROUP_ICONS[group.icon] ?? <Package className="h-5 w-5" />,
  permission: group.permission,
  excludePermission: group.excludePermission,
  blurb: group.blurb,
  badge: group.code === 'CMD' ? ('approvals' as const) : undefined,
  children: group.children
    .filter((c) => !c.hidden)
    .map((c) => ({
      title: c.title,
      href: c.path,
      description: c.purpose,
    })),
}));

/**
 * Shared shape for a rail button/link.
 *
 * Expanded sets the icon beside the label and aligns left, which is what makes
 * the extra width worth spending; the narrower two centre a stacked icon.
 */
function railItemClass(active: boolean, width: NavWidth) {
  return cn(
    'flex w-full items-center rounded-lg transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    width === 'expanded' ? 'flex-row gap-3 px-3 py-2.5 text-left' : 'flex-col gap-1 px-1 py-2.5',
    width === 'collapsed' && 'justify-center py-3',
    // Resting items are dimmed so the current one is the only thing at full
    // strength. An earlier attempt did this with a flat 70% opacity and dropped
    // six of the light rail tints below AA; --sidebar-foreground-muted is
    // computed per rail as the quietest value that still clears it, so the
    // hierarchy cannot cost contrast again. Hovering restores full strength,
    // which is what makes the rail feel responsive rather than dim.
    active
      ? 'bg-sidebar-accent text-sidebar-primary font-medium'
      : 'text-sidebar-foreground-muted hover:bg-sidebar-hover hover:text-sidebar-foreground'
  );
}

/**
 * The item's label. Hidden when collapsed, so each item carries its name in
 * aria-label instead — an icon with no accessible name is unusable by screen
 * reader and unlabelled to anyone who does not recognise the glyph.
 */
function RailLabel({ title, width }: { title: string; width: NavWidth }) {
  if (width === 'collapsed') return null;
  return (
    <span
      className={cn(
        'font-medium leading-tight',
        width === 'expanded' ? 'text-sm' : 'text-center text-[10px]'
      )}
    >
      {title}
    </span>
  );
}

export function MainLayout() {
  const { appearance } = useTheme();
  const navWidth = appearance.navWidth;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  /**
   * The nav group whose flyout is open, plus the vertical offset of its rail
   * button within the sidebar. The panel is rendered as a child of <aside>
   * rather than inside <nav>: nav scrolls vertically, and `overflow-y: auto`
   * forces `overflow-x` to compute to `auto` too, which clips anything reaching
   * past the 88px rail.
   */
  const [openGroup, setOpenGroup] = useState<{ title: string; top: number } | null>(null);
  const sidebarRef = useRef<HTMLElement>(null);
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * The rail a person is actually allowed to use.
   *
   * A group survives if any of its children do — filtering the leaves first
   * means a group never appears with an empty flyout, and a group whose own
   * permission is missing disappears even if a child would have passed.
   */
  const visibleNavigation = useMemo(
    () =>
      navigation
        .filter((item) => !item.permission || hasPermission(item.permission))
        // A wildcard permission satisfies every gate, so the customer portal
        // showed up for staff. This is the one group that is defined by what the
        // reader is *not*.
        .filter((item) => !item.excludePermission || !hasPermission(item.excludePermission))
        .map((item) => ({
          ...item,
          children: item.children?.filter(
            (child) => !child.permission || hasPermission(child.permission)
          ),
        }))
        .filter((item) => item.href || (item.children?.length ?? 0) > 0),
    [hasPermission]
  );

  // Real-time alerts
  const { isConnected, unreadCount } = useAlerts(user?.companyId);

  /**
   * Pending approvals across purchase orders, consumption variances and freight
   * awards. Fetched once per mount rather than polled — a count that is a few
   * minutes stale still answers "is anything waiting on me", which is the only
   * question the badge exists to answer.
   */
  const [pendingApprovals, setPendingApprovals] = useState(0);
  const canSeeApprovals = hasPermission('approval:read');

  const onApprovalsPage = location.pathname === '/approvals';

  useEffect(() => {
    if (!canSeeApprovals) return;
    let cancelled = false;
    approvalsApi
      .getPendingSummary()
      .then((s) => !cancelled && setPendingApprovals(s.total))
      .catch(() => !cancelled && setPendingApprovals(0));
    return () => {
      cancelled = true;
    };
  }, [canSeeApprovals, onApprovalsPage]);

  const isActive = (href: string) => location.pathname === href;
  const isParentActive = (children?: { href: string }[]) =>
    children?.some((child) => location.pathname === child.href);

  // Navigating away should dismiss both the flyout and the mobile drawer.
  useEffect(() => {
    setOpenGroup(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Dismiss the flyout on Escape or a click outside the rail.
  useEffect(() => {
    if (!openGroup) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenGroup(null);
    };
    const onPointerDown = (e: MouseEvent) => {
      if (!sidebarRef.current?.contains(e.target as Node)) setOpenGroup(null);
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [openGroup]);

  return (
    <div className="flex h-screen bg-background">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:p-4 focus:bg-card focus:text-foreground focus:shadow-floating focus:rounded-lg focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Backdrop behind the mobile drawer */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-30 bg-foreground/20 lg:hidden"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar — a fixed icon rail; groups open their children in a flyout,
          since 43 destinations cannot fit in the rail itself.
          Sits above the mobile backdrop (z-30), or taps on nav items would land
          on the backdrop's dismiss handler instead of navigating — and below
          Radix overlays (z-50) so dialogs still cover the rail. */}
      <aside
        ref={sidebarRef}
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground',
          'transition-[width] duration-200',
          'transition-transform duration-200 lg:relative lg:translate-x-0',
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        style={{ width: NAV_WIDTHS[navWidth] }}
      >
        {/* Logo */}
        <Link
          to="/"
          className="flex h-16 shrink-0 items-center justify-center border-b border-sidebar-border"
          aria-label="FieldPulse home"
        >
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-sm font-bold text-primary-foreground">
            F
          </div>
        </Link>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-2 py-2" aria-label="Main navigation">
          {visibleNavigation.map((item) => {
            const active = item.href ? isActive(item.href) : !!isParentActive(item.children);
            const isOpen = openGroup?.title === item.title;

            return (
              <div key={item.title} className="relative mb-0.5">
                {/*
                  Where you are, said in shape as well as colour. The active
                  item was signalled by hue alone, which asks the reader to
                  know the palette and disappears entirely for anyone who
                  cannot separate the two. aria-hidden because aria-current on
                  the link already says it to a screen reader.
                */}
                {active && (
                  <span
                    aria-hidden
                    className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-sidebar-primary"
                  />
                )}
                {item.href ? (
                  <Link
                    to={item.href}
                    aria-current={active ? 'page' : undefined}
                    className={railItemClass(active, navWidth)}
                    aria-label={navWidth === 'collapsed' ? item.title : undefined}
                    title={navWidth === 'collapsed' ? item.title : undefined}
                  >
                    {item.icon}
                    <RailLabel title={item.title} width={navWidth} />
                    {item.badge === 'approvals' && pendingApprovals > 0 && (
                      <span
                        className={cn(
                          'rounded-full bg-primary px-1.5 py-px text-[10px] font-semibold leading-tight text-primary-foreground tabular-nums',
                          // Expanded has room on the row; the narrower rails
                          // pin it to the corner of the item, which is why the
                          // wrapper carries `relative`.
                          navWidth === 'expanded' ? 'ml-auto' : 'absolute right-0.5 top-0.5'
                        )}
                      >
                        {pendingApprovals > 99 ? '99+' : pendingApprovals}
                      </span>
                    )}
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      if (isOpen) {
                        setOpenGroup(null);
                        return;
                      }
                      const button = e.currentTarget.getBoundingClientRect();
                      const sidebar = sidebarRef.current?.getBoundingClientRect();
                      setOpenGroup({
                        title: item.title,
                        top: button.top - (sidebar?.top ?? 0),
                      });
                    }}
                    aria-expanded={isOpen}
                    aria-current={active ? 'true' : undefined}
                    aria-haspopup="true"
                    aria-label={navWidth === 'collapsed' ? item.title : undefined}
                    title={navWidth === 'collapsed' ? item.title : undefined}
                    className={railItemClass(active, navWidth)}
                  >
                    {item.icon}
                    <RailLabel title={item.title} width={navWidth} />
                  </button>
                )}
              </div>
            );
          })}
        </nav>

        {/* Flyout lives outside <nav> so the rail's vertical scrolling cannot
            clip it horizontally. */}
        {openGroup && (
          <div
            className={cn(
              'absolute left-full z-10 ml-1 w-80 rounded-lg border border-border',
              'bg-popover p-1 shadow-overlay max-h-[70vh] overflow-y-auto'
            )}
            style={{ top: openGroup.top }}
          >
            {(() => {
              const group = visibleNavigation.find((item) => item.title === openGroup.title);
              return (
                <>
                  <div className="px-2 pb-1.5 pt-2">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {openGroup.title}
                    </p>
                    {/* The group's own line. Without it a reader has to infer
                        what "Compliance" or "Movement" covers from its members,
                        which is the wrong way round. */}
                    {group?.blurb && (
                      <p className="mt-1 text-xs leading-snug text-muted-foreground">
                        {group.blurb}
                      </p>
                    )}
                  </div>
                  <div className="my-1 border-t" />
                  {group?.children?.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className={cn(
                        'block rounded-md px-2 py-1.5 transition-colors',
                        isActive(child.href)
                          ? 'bg-primary/10 text-primary'
                          : 'text-foreground hover:bg-muted'
                      )}
                    >
                      <span className={cn('block text-sm', isActive(child.href) && 'font-medium')}>
                        {child.title}
                      </span>
                      {child.description && (
                        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                          {child.description}
                        </span>
                      )}
                    </Link>
                  ))}
                </>
              );
            })()}
          </div>
        )}
      </aside>

      {/* Main content.
          min-w-0 lets this column shrink below its content's intrinsic width —
          a flex child defaults to min-width:auto, so wide content pushes the
          column past the viewport instead of fitting inside it. Paired with
          overflow-x-hidden that produced a silent clip: the right-hand side
          was cut with no scrollbar to reach it. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="flex h-16 items-center justify-between border-b border-border bg-card px-4 lg:px-8">
          <div className="flex items-center gap-4">
            <Button
              aria-label="Open navigation menu"
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="relative hidden md:block">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search... (⌘K)"
                className="h-9 w-64 rounded-lg border border-input bg-muted pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <Button
              aria-label="View alerts"
              variant="ghost"
              size="icon"
              className="relative hover:bg-muted"
              onClick={() => navigate('/alerts')}
              title={`${unreadCount} unread alerts`}
            >
              <Bell className="h-5 w-5 text-muted-foreground" />
              {unreadCount > 0 && (
                <Badge
                  className="absolute -right-1 -top-1 h-5 w-5 rounded-full bg-danger-500 text-white text-xs flex items-center justify-center p-0 border-2 border-white"
                  variant="destructive"
                >
                  {unreadCount > 99 ? '99+' : unreadCount}
                </Badge>
              )}
              {isConnected && (
                <span
                  className="absolute bottom-0 right-0 h-2 w-2 rounded-full bg-success-500 border border-white"
                  title="Real-time alerts connected"
                />
              )}
            </Button>

            {/* Appearance lives in a Popover, not a DropdownMenu: menu
                semantics (roving focus, close-on-activate) fight with the
                interactive controls inside, and detach them mid-click. */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Appearance settings">
                  <Palette className="h-5 w-5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-auto p-0">
                <AppearancePanel />
              </PopoverContent>
            </Popover>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 hover:bg-muted">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-bold">
                    {user?.fullName
                      ?.split(' ')
                      .map((n) => n[0])
                      .join('')
                      .toUpperCase() || 'U'}
                  </div>
                  <span className="hidden md:inline-block text-sm font-medium text-foreground">
                    {user?.fullName || 'User'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.fullName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <User className="mr-2 h-4 w-4" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        {/* overflow-x-auto, not hidden: if a page ever does exceed the width,
            it must stay reachable by scrolling rather than being cut off. */}
        <main
          id="main-content"
          tabIndex={-1}
          className="flex-1 overflow-y-auto overflow-x-auto p-4 lg:px-8 lg:py-6"
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
